import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { MAX_EBOOK_BYTES } from "@/lib/constants";
import { Mail, Trash2, Download, Upload } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const EMPTY_FORM = {
  title: "",
  issue_date: format(new Date(), "yyyy-MM-dd"),
  notes: "",
  file_data: "",
  file_name: "",
  mime_type: "",
};

export default function LessonsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/lessons");
      setLessons(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_EBOOK_BYTES) {
      toast.error("Please choose a file under 8 MB");
      return;
    }
    const data = await fileToDataUrl(file);
    setForm((f) => ({
      ...f,
      file_data: data,
      file_name: file.name,
      mime_type: file.type,
      title: f.title || file.name.replace(/\.[^.]+$/, ""),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.file_data) {
      toast.error("Add a title and choose a file");
      return;
    }
    setSaving(true);
    try {
      await api.post("/lessons", form);
      toast.success("Lesson uploaded");
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadLesson = async (l) => {
    try {
      const { data } = await api.get(`/lessons/${l.id}`);
      const a = document.createElement("a");
      a.href = data.file_data;
      a.download = data.file_name || "lesson";
      a.click();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/lessons/${id}`);
    load();
  };

  return (
    <div className="space-y-10" data-testid="lessons-page">
      <SectionTitle overline="Learn" title="YDD Newsletters" />

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Mail size={22} />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold tracking-tight">YDD newsletter library</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isAdmin
                  ? "Upload Your Dog Days newsletters and lesson PDFs — everyone with an account can read them."
                  : "Your Dog Days newsletters and lessons. Open any one to read."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className={`grid grid-cols-1 ${isAdmin ? "lg:grid-cols-3" : ""} gap-6`}>
        <Card className={`${isAdmin ? "lg:col-span-2" : ""} border-border shadow-none rounded-lg`}>
          <CardContent className="p-0">
            {loading ? (
              <p className="text-sm text-muted-foreground p-10 text-center">Loading…</p>
            ) : lessons.length === 0 ? (
              <p className="text-sm text-muted-foreground p-10 text-center" data-testid="lessons-empty">
                No lessons uploaded yet — add your first newsletter →
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {lessons.map((l) => (
                  <li key={l.id} className="p-5 flex items-start gap-4" data-testid={`lesson-row-${l.id}`}>
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                      <Mail size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{l.title}</p>
                      {l.issue_date ? (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Issued {format(parseISO(l.issue_date), "MMM d, yyyy")}
                        </p>
                      ) : null}
                      <p className="text-[11px] text-muted-foreground/80 mt-0.5">{l.file_name}</p>
                      {l.notes ? <p className="text-sm text-foreground/80 mt-2">{l.notes}</p> : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadLesson(l)}
                        className="p-2 hover:bg-muted rounded-md"
                        title="Download"
                        data-testid={`download-lesson-${l.id}`}
                      >
                        <Download size={16} />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => remove(l.id)}
                          className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive"
                          data-testid={`delete-lesson-${l.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <p className="font-medium mb-4">Upload lesson</p>
            <form onSubmit={submit} className="space-y-3" data-testid="add-lesson-form">
              <div>
                <Label htmlFor="ls-file">File (PDF / EPUB / DOCX, ≤ 8 MB)</Label>
                <Input
                  id="ls-file"
                  data-testid="lesson-file-input"
                  type="file"
                  accept="application/pdf,application/epub+zip,.epub,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={onFile}
                  className="mt-1.5"
                />
                {form.file_name ? (
                  <p className="text-xs text-muted-foreground mt-1.5 truncate">Selected: {form.file_name}</p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="ls-title">Title</Label>
                <Input
                  id="ls-title"
                  data-testid="lesson-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="mt-1.5"
                  placeholder="e.g. January Newsletter"
                />
              </div>
              <div>
                <Label htmlFor="ls-date">Issue date</Label>
                <Input
                  id="ls-date"
                  data-testid="lesson-date-input"
                  type="date"
                  value={form.issue_date}
                  onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="ls-notes">Notes</Label>
                <Textarea
                  id="ls-notes"
                  data-testid="lesson-notes-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1.5"
                  placeholder="What it's about, things to revisit…"
                />
              </div>
              <Button
                type="submit"
                disabled={saving || !form.file_data}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-lesson-button"
              >
                <Upload size={14} className="mr-1.5" />
                {saving ? "Uploading…" : "Upload lesson"}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
